<template>
  <div v-rect-loading="loading" class="ai-agent-editor-container">
    <div class="ai-agent-editor-header">
      <div class="ai-agent-editor-title">{{ $t(`${i18nPrefix}.menu.edit`) }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="form"
        class="ai-agent-info"
        label-width="220px"
        :model="aiAgentInfo"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { getI18nPropertyValue } from '@/i18n'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { COMPANY_STATUS } from '@/constant/company'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.aiAgentManagement',
      loading: false,
      aiAgentInfo: {
        rootCompanyId: null,
        modelId: null,
        agentName: null,
        description: null
      },
      aiModelList: [],
      rootCompanyList: []
    }
  },
  computed: {
    aiAgentId () {
      return this.$route.params.id
    },
    formLines () {
      return {
        // rootCompanyId: {
        //   disabled: this.aiAgentInfo.rootCompanyId,
        //   elType: 'el-select',
        //   label: this.$t(`${this.i18nPrefix}.label.companyName`),
        //   option: {
        //     data: this.rootCompanyList,
        //     valueKey: 'id',
        //     labelKey: 'i18nName'
        //   },
        //   props: {
        //     filterable: true,
        //     clearable: true
        //   }
        // },
        agentName: {
          label: this.$t(`${this.i18nPrefix}.label.agentName`),
          disabled: true
        },
        description: {
          label: this.$t(`${this.i18nPrefix}.label.agentDescription`)
        },
        modelId: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.modelName`),
          option: {
            data: this.aiModelList,
            valueKey: 'id',
            labelKey: 'name'
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        temperature: {
          elType: 'el-slider',
          label: this.$t(`${this.i18nPrefix}.label.temperature`),
          props: {
            min: 0,
            max: 2,
            step: 0.01,
            showInput: true
          }
        },
        prompt: {
          label: this.$t(`${this.i18nPrefix}.label.prompt`),
          props: {
            type: 'textarea',
            autosize: {
              minRows: 20,
              maxRows: 26
            }
          }
        }
      }
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('ai', ['getAIAgentInfoById', 'getAIModelList', 'updateAIAgent']),
    ...mapActions('superAdmin', ['getSuperAdminCompanyList']),
    initPage () {
      this.loadAiAgentInfo()
      this.loadAIModelList()
      // this.loadCompanyList()
    },
    async loadAiAgentInfo () {
      if (!this.aiAgentId) {
        return
      }
      this.loading = true
      try {
        const resp = await this.getAIAgentInfoById(this.aiAgentId)
        if (resp) {
          const rootCompanyId = resp.root_company?.id
          const agentName = resp.agent.name
          const description = resp.agent.description
          const modelId = resp.model.id

          this.aiAgentInfo = {
            rootCompanyId,
            agentName,
            description,
            modelId,
            temperature: resp.temperature,
            prompt: resp.prompt
          }
        }
      } finally {
        this.loading = false
      }
    },
    async loadAIModelList () {
      this.loading = true
      try {
        const resp = await this.getAIModelList()
        if (resp) {
          this.aiModelList = resp
        }
      } finally {
        this.loading = false
      }
    },
    async loadCompanyList () {
      const resp = await this.getSuperAdminCompanyList({ only_root_company: 1, status: COMPANY_STATUS.APPROVED })
      if (resp) {
        this.rootCompanyList = _.map(resp.result, com => {
          if (!com.cn_name) {
            com.cn_name = com.name
          }
          if (!com.name) {
            com.name = com.cn_name
          }
          Object.defineProperty(com, 'i18nName', {
            configurable: true,
            enumerable: true,
            get () {
              const i18nName = getI18nPropertyValue(com, 'cn_name', 'name')
              return `${com.id} - ${i18nName || ''}`
            }
          })
          return com
        })
      }
    },
    handleCancel () {
      this.$router.go(-1)
    },
    async handleSave () {
      const isValid = this.$refs.form.submit()
      if (!isValid) {
        return
      }
      const params = {
        id: this.aiAgentId,
        root_company_id: this.aiAgentInfo.rootCompanyId,
        model_id: this.aiAgentInfo.modelId,
        temperature: this.aiAgentInfo.temperature,
        prompt: this.aiAgentInfo.prompt,
        description: this.aiAgentInfo.description
      }
      this.loading = true
      try {
        const resp = await this.updateAIAgent(params)
        if (resp) {
          this.$router.go(-1)
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-agent-editor-container {
  .ai-agent-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;
    .ai-agent-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }
  .form-container {
    width: 1000px;
    margin: 30px auto 0;
    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
